import React, { useMemo } from 'react';
import {
  Box,
  capitalizeFirstLetter,
  Inscription,
  ListItem,
  ListItemBase,
  ListItemType,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import REGEXP from '@/common/regexp';
import { DropButton } from '@/components/controls/DropButton';
import {
  CreationSourceType,
  Dependent,
} from '@/modules/user/components/ManageProfile/components/Dependents/dependents.types';
import { useStore } from '@/modules/user/components/ManageProfile/components/Dependents/stores/useDependents.store';
import theme from '@/styles/theme';

import DependentsAge from '../../../DependentsAge';

const FIELD_PLACEHOLDER = '-';

export const isEditDisabled = (val?: CreationSourceType) =>
  val === CreationSourceType.API ||
  val === CreationSourceType.CARRIER ||
  val === CreationSourceType.DEPENDENT;

export const EDIT_DISABLED_HINT =
  'Dependents added by the employer cannot be edited. Contact your employer to make any changes.';

export const HEADERS: ListItemBase[] = [
  { key: 'firstName', title: 'First name' },
  { key: 'secondName', title: 'Second name' },
  { key: 'dateOfBirth', title: 'Date of birth' },
  { key: 'relationship', title: 'Relationship' },
];

const highlightedRowStyle = {
  borderSize: '2px',
  borderColor: theme.colors.accentActive,
  boxShadow: theme.shadows.highlighted['box-shadow'],
};

interface Props {
  dependents?: Dependent[];
  highlightedDependentId?: string;
  onDeactivate: () => void;
  onEdit: () => void;
}

const useDependentsList = ({ dependents, highlightedDependentId, onDeactivate, onEdit }: Props) => {
  const sortedDependents = useMemo(() => {
    if (!dependents?.length) {
      return [];
    }

    return [...dependents].sort((prev, next) => {
      const firstNamePrev = prev?.firstName || '';
      const firstNameNext = next?.firstName || '';
      return firstNamePrev.localeCompare(firstNameNext);
    });
  }, [dependents]);

  const { handleSetEditableDependent } = useStore();

  const formattedList = useMemo<ListItem[]>(
    () =>
      sortedDependents?.map((dependent) => ({
        id: `${dependent.id}`,
        rowStyle: {
          minHeight: 76,
          ...(highlightedDependentId && highlightedDependentId === dependent.id ? highlightedRowStyle : {}),
        },
        dropAlign: { bottom: 'top', right: 'right' },
        fields: [
          {
            key: 'firstName',
            type: ListItemType.Text,
            title: dependent.firstName || FIELD_PLACEHOLDER,
            flex: 1,
            style: { fontSize: theme.fonts.text16Medium['font-size'] },
          },
          {
            key: 'secondName',
            type: ListItemType.Text,
            title: dependent.lastName || FIELD_PLACEHOLDER,
            flex: 1,
            style: { fontSize: theme.fonts.text16Medium['font-size'] },
          },
          {
            key: 'dateOfBirth',
            type: ListItemType.Node,
            title: '',
            node: (
              <Inscription size="large" color="textBody">
                {dependent?.birthOn
                  ? dayjs(dependent.birthOn).format(DEFAULT_DATE_FORMAT)
                  : FIELD_PLACEHOLDER}
                {dependent?.birthOn ? <DependentsAge birthOn={dependent.birthOn} /> : null}
              </Inscription>
            ),
            flex: 1,
            style: { fontSize: theme.fonts.text16Medium['font-size'] },
          },
          {
            key: 'relationship',
            type: ListItemType.Text,
            title:
              capitalizeFirstLetter(dependent?.relationshipType?.replace(REGEXP.DASH_SYMBOL, ' ') || '') ||
              FIELD_PLACEHOLDER,
            flex: 1,
            style: { fontSize: theme.fonts.text16Medium['font-size'] },
          },
          {
            key: 'options',
            type: ListItemType.Node,
            title: '',
            flex: 'none',
            fieldStyle: { right: theme.spacings.spacing24, position: 'absolute' },
            node: (
              <DropButton
                testId="dependentsListOptions"
                contentStyle={{
                  minWidth: '150px',
                  top: '-95px',
                  left: '-120px',
                }}
              >
                <Box pad={{ vertical: 'spacing6', horizontal: 'spacing8' }} justify="center">
                  <Inscription
                    cursor="pointer"
                    {...(isEditDisabled(dependent?.creationSourceType)
                      ? {
                          color: 'textDisabled',
                          title: EDIT_DISABLED_HINT,
                        }
                      : {
                          color: 'textBody',
                          onClick: () => {
                            onEdit();
                            handleSetEditableDependent(dependent);
                          },
                        })}
                  >
                    Edit
                  </Inscription>
                </Box>
                <Box pad={{ vertical: 'spacing6', horizontal: 'spacing8' }} justify="center">
                  <Inscription
                    color="danger"
                    cursor="pointer"
                    onClick={() => {
                      onDeactivate();
                      handleSetEditableDependent(dependent);
                    }}
                  >
                    Deactivate
                  </Inscription>
                </Box>
              </DropButton>
            ),
          },
        ],
      })) || [],
    [sortedDependents, highlightedDependentId, handleSetEditableDependent, onDeactivate, onEdit]
  );

  return formattedList;
};

export default useDependentsList;
